/**
 * Utility function to track bot check results with queue support
 */

import { BotCheckCollector } from '../collectors/BotCheckCollector';
import { EventQueue, EventProcessor } from './CollectorEventQueue';

// Bot check event data structure
interface BotCheckEventData {
  botCheckResult: {
    isBot: boolean;
    isBotBypass: boolean;
    isBotBypassReason?: string;
    reason?: string;
    reasonCode?: string;
    score?: number;
  };
  success: boolean;
}

// Processor for bot check events
class BotCheckProcessor implements EventProcessor<BotCheckEventData> {
  process(data: BotCheckEventData): void {
    const collector = BotCheckCollector.getInstance();
    if (collector) {
      collector.trackLoginBotCheck(data.botCheckResult, data.success);
    } else {
      throw new Error('BotCheckCollector not available');
    }
  }

  isReady(): boolean {
    return BotCheckCollector.getInstance() !== null;
  }
}

// Create the bot check event queue
const botCheckQueue = new EventQueue<BotCheckEventData>({
  name: 'BotCheck',
  maxQueueSize: 50,
  maxAge: 5 * 60 * 1000, // 5 minutes
  flushIntervalMs: 5000 // 5 seconds
});

// Set up the processor
botCheckQueue.setProcessor(new BotCheckProcessor());

/**
 * Track bot check result for login attempts
 */
export function trackLoginBotCheck(
  botCheckResult:
    | {
        isBot: boolean;
        isBotBypass: boolean;
        isBotBypassReason?: string;
        reason?: string;
        reasonCode?: string;
        score?: number;
      }
    | undefined
    | null,
  success: boolean
): void {
  if (!botCheckResult) {
    return;
  }

  try {
    const eventData: BotCheckEventData = {
      botCheckResult,
      success
    };

    // Use the queue - it will either process immediately or queue for later
    botCheckQueue.enqueue(eventData);
  } catch (error) {
    console.error('[trackBotCheck] Error tracking bot check:', error);
  }
}

// Export queue for debugging/testing purposes
export { botCheckQueue };
